// features/admin/deliveryApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export interface DeliveryModel {
  id: number;
  model_id?: number;
  model_name: string;
  delivery_type: number;
  hours?: number | null;
  days?: number | null;
  display_text: string;
  status: number;
  is_default: number;
  is_restricted: number;
  delivery_charge?: string;
  created_at?: string;
  createddt?: string;
  updated_at?: string;
  updateddt?: string;
}

export interface DeliveryModelResponse {
  error: boolean;
  message: string;
  result?: DeliveryModel[];
  data?: DeliveryModel[];
}

export interface DeliveryModelRequest {
  model_name: string;
  delivery_type: number;
  hours?: number;
  days?: number;
  display_text: string;
  status?: number;
  is_default?: number;
  is_restricted?: number;
  delivery_charge?: string;
}

export interface UpdateDeliveryModelRequest extends DeliveryModelRequest {
  model_id: number;
}

export interface DeleteDeliveryModelRequest {
  model_id: number;
}

export const deliveryApi = createApi({
  reducerPath: 'deliveryApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_ENDPOINT,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('admin_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      headers.set('Content-Type', 'application/x-www-form-urlencoded');
      return headers;
    },
  }),
  tagTypes: ['DeliveryModels'],
  endpoints: (build) => ({
    listDeliveryModels: build.query<DeliveryModelResponse, void>({
      query: () => ({
        url: 'globalsettings/listDeliveryModel',
        method: 'POST',
        body: new URLSearchParams(),
      }),
      providesTags: ['DeliveryModels'],
      transformResponse: (response: any) => {
        console.log('List API Response:', response);
        
        // Handle both response structures: result or data
        let deliveryModels = [];
        
        if (Array.isArray(response.result)) {
          deliveryModels = response.result;
        } else if (Array.isArray(response.data)) {
          deliveryModels = response.data;
        } else if (Array.isArray(response)) {
          deliveryModels = response;
        }
        
        // Ensure each model has proper id
        const formattedModels = deliveryModels.map((model: any) => ({
          ...model,
          id: model.id || model.model_id || Math.random(), // Ensure id exists
          model_id: model.model_id || model.id,
          hours: model.hours === null ? undefined : model.hours,
          days: model.days === null ? undefined : model.days,
        }));
        
        return {
          error: response.error || false,
          message: response.message || '',
          result: formattedModels,
          data: formattedModels,
        };
      },
    }),

    addDeliveryModel: build.mutation<DeliveryModelResponse, DeliveryModelRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('model_name', data.model_name.trim());
        formData.append('delivery_type', data.delivery_type.toString());
        
        if (data.delivery_type === 0 && data.hours != null) {
          formData.append('hours', data.hours.toString());
        } else if (data.delivery_type === 1 && data.days != null) {
          formData.append('days', data.days.toString());
        }
        
        formData.append('display_text', data.display_text.trim());
        formData.append('status', (data.status || 1).toString());
        formData.append('is_default', (data.is_default || 0).toString());
        formData.append('is_restricted', (data.is_restricted || 1).toString());
        
        // Add delivery charge if provided
        if (data.delivery_charge) {
          formData.append('delivery_charge', data.delivery_charge);
        }

        return {
          url: 'globalsettings/newDeliveryModel',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['DeliveryModels'],
      // Optimistic update
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          console.log('Add Delivery Response:', data);
          
          // Force refetch the list
          dispatch(deliveryApi.util.invalidateTags(['DeliveryModels']));
        } catch (error) {
          console.error('Add delivery failed:', error);
        }
      },
    }),

    updateDeliveryModel: build.mutation<DeliveryModelResponse, UpdateDeliveryModelRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('model_id', data.model_id.toString());
        formData.append('model_name', data.model_name.trim());
        formData.append('delivery_type', data.delivery_type.toString());
        
        if (data.delivery_type === 0 && data.hours != null) {
          formData.append('hours', data.hours.toString());
        } else if (data.delivery_type === 1 && data.days != null) {
          formData.append('days', data.days.toString());
        }
        
        formData.append('display_text', data.display_text.trim());
        formData.append('status', (data.status || 1).toString());
        formData.append('is_default', (data.is_default || 0).toString());
        formData.append('is_restricted', (data.is_restricted || 1).toString());
        
        // Add delivery charge if provided
        if (data.delivery_charge) {
          formData.append('delivery_charge', data.delivery_charge);
        }

        return {
          url: 'globalsettings/updateDeliveryModel',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['DeliveryModels'],
    }),

    deleteDeliveryModel: build.mutation<DeliveryModelResponse, DeleteDeliveryModelRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('model_id', data.model_id.toString());

        return {
          url: 'globalsettings/deleteDeliveryModel',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['DeliveryModels'],
    }),
  }),
});

export const {
  useListDeliveryModelsQuery,
  useAddDeliveryModelMutation,
  useUpdateDeliveryModelMutation,
  useDeleteDeliveryModelMutation,
} = deliveryApi;